import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, Tag, Sparkles } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useLanguage } from "../i18n/LanguageContext";
import { BLOG_ARTICLES, getArticleI18n } from "../data/blogArticles";
import { getBlogI18n } from "../i18n/blog";

const BlogIndexPage = () => {
  const { lang } = useLanguage();
  const ui = getBlogI18n(lang);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white antialiased" data-testid="blog-index-page">
      <Navbar />

      {/* HERO */}
      <section className="relative w-full overflow-hidden pb-12 pt-32 md:pt-40">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(251,191,36,0.16),transparent_55%)]" />
        <div className="absolute inset-x-0 top-0 mx-auto h-px max-w-5xl bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
            maskImage: "radial-gradient(circle at 50% 0%, black, transparent 70%)",
          }}
        />

        <div className="relative mx-auto max-w-6xl px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full border border-amber-400/25 bg-white/[0.03] px-3 py-1.5 backdrop-blur"
          >
            <Sparkles className="h-3.5 w-3.5 text-amber-300" />
            <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-amber-300 md:text-xs">
              {ui.eyebrow}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="mt-6 font-display text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl"
            data-testid="blog-index-title"
          >
            {ui.index_title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-4 max-w-3xl text-base leading-relaxed text-white/55 md:text-lg"
          >
            {ui.index_subtitle}
          </motion.p>
        </div>
      </section>

      {/* GRID */}
      <section className="relative w-full bg-black pb-32">
        <div className="mx-auto max-w-6xl px-6 md:px-12">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {BLOG_ARTICLES.map((article, idx) => {
              const a = getArticleI18n(article, lang);
              return (
                <motion.article
                  key={article.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: idx * 0.06 }}
                  className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.025] backdrop-blur-xl transition-all hover:-translate-y-1 hover:border-amber-400/30 hover:bg-white/[0.05]"
                  data-testid={`blog-card-${article.slug}`}
                >
                  <Link to={`/blog/${article.slug}`} className="flex h-full flex-col">
                    <div className="relative h-44 w-full overflow-hidden">
                      <img
                        src={article.cover}
                        alt={a.title}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                      <div className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full border border-amber-400/30 bg-black/60 px-2.5 py-1 backdrop-blur">
                        <Tag className="h-3 w-3 text-amber-300" />
                        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-200">
                          {ui.categories[article.category] || article.category}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-1 flex-col p-6">
                      <h2 className="font-display text-lg font-bold leading-tight text-white transition-colors group-hover:text-amber-100">
                        {a.title}
                      </h2>
                      <p className="mt-3 text-sm leading-relaxed text-white/55 line-clamp-3">
                        {a.excerpt}
                      </p>

                      <div className="mt-5 flex items-center justify-between border-t border-white/5 pt-4 text-[11px] uppercase tracking-[0.22em] text-white/40">
                        <span className="inline-flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5" />
                          {article.readTime} {ui.min_read}
                        </span>
                        <span className="inline-flex items-center gap-1 text-amber-300 transition-transform group-hover:translate-x-1">
                          {ui.read_more}
                          <ArrowRight className="h-3 w-3" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              );
            })}
          </div>

          {/* Disclaimer */}
          <p className="mt-12 text-center text-[11px] uppercase tracking-[0.22em] text-white/30">
            {ui.disclaimer}
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogIndexPage;
